import React, { useRef, useEffect, useState } from 'react';
import {
  Paper,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link,
  Image,
  Code,
  Undo,
  Redo,
  Title,
  FormatColorText,
  FormatColorFill
} from '@mui/icons-material';
import { YouTube } from '@mui/icons-material';

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing...' }) => {
  const editorRef = useRef(null);
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageDialog, setImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [youtubeDialog, setYoutubeDialog] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleInput = (e) => {
    // Force LTR direction to fix RTL typing issue
    e.target.style.direction = 'ltr';
    e.target.style.textAlign = 'left';
    handleContentChange();
  };

  const execCommand = (command, val = null) => {
    editorRef.current?.focus();
    try {
      document.execCommand(command, false, val);
      handleContentChange();
    } catch (error) {
      console.warn('Command not supported:', command);
    }
  };

  const insertHTML = (html) => {
    editorRef.current?.focus();
    try {
      document.execCommand('insertHTML', false, html);
      handleContentChange();
    } catch (error) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const div = document.createElement('div');
        div.innerHTML = html;
        const frag = document.createDocumentFragment();
        while (div.firstChild) {
          frag.appendChild(div.firstChild);
        }
        range.insertNode(frag);
        handleContentChange();
      }
    }
  };

  const handleLinkInsert = () => {
    if (linkUrl) {
      const html = `<a href="${linkUrl}" target="_blank">${linkText || linkUrl}</a>`;
      insertHTML(html);
      setLinkDialog(false);
      setLinkUrl('');
      setLinkText('');
    }
  };

  const handleImageInsert = () => {
    if (imageUrl) {
      const html = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      insertHTML(html);
      setImageDialog(false);
      setImageUrl('');
      setImageAlt('');
    }
  };

  const handleYouTubeInsert = () => {
    if (youtubeUrl) {
      const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (videoId) {
        const html = `<div class="youtube-embed" style="margin: 10px 0;">
          <iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId[1]}" 
            frameborder="0" allowfullscreen style="max-width: 100%; height: auto;"></iframe>
        </div>`;
        insertHTML(html);
      } else {
        insertHTML(`<p><a href="${youtubeUrl}" target="_blank">${youtubeUrl}</a></p>`);
      }
      setYoutubeDialog(false);
      setYoutubeUrl('');
    }
  };

  const formatBlock = (tag) => {
    execCommand('formatBlock', `<${tag}>`);
  };

  const handleTextColor = (color) => {
    execCommand('foreColor', color);
  };

  const handleBgColor = (color) => {
    execCommand('hiliteColor', color);
  };

  const toolbarButtons = [
    { icon: <Title />, action: () => formatBlock('h2'), tooltip: 'Heading' },
    { icon: <FormatBold />, command: 'bold', tooltip: 'Bold (Ctrl+B)' },
    { icon: <FormatItalic />, command: 'italic', tooltip: 'Italic (Ctrl+I)' },
    { icon: <FormatUnderlined />, command: 'underline', tooltip: 'Underline (Ctrl+U)' },
    { 
      icon: <FormatColorText />, 
      tooltip: 'Text Color',
      custom: (
        <Box display="flex" alignItems="center">
          <input
            type="color"
            value={textColor}
            onChange={(e) => {
              setTextColor(e.target.value);
              handleTextColor(e.target.value);
            }}
            style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
            id="text-color-picker"
          />
          <IconButton
            size="small"
            onClick={() => document.getElementById('text-color-picker').click()}
            sx={{ border: '1px solid transparent', '&:hover': { border: '1px solid #ccc' } }}
          >
            <FormatColorText />
          </IconButton>
        </Box>
      )
    },
    { 
      icon: <FormatColorFill />, 
      tooltip: 'Background Color',
      custom: (
        <Box display="flex" alignItems="center">
          <input
            type="color"
            value={bgColor}
            onChange={(e) => {
              setBgColor(e.target.value);
              handleBgColor(e.target.value);
            }}
            style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
            id="bg-color-picker"
          />
          <IconButton
            size="small"
            onClick={() => document.getElementById('bg-color-picker').click()}
            sx={{ border: '1px solid transparent', '&:hover': { border: '1px solid #ccc' } }}
          >
            <FormatColorFill />
          </IconButton>
        </Box>
      )
    },
    { icon: <FormatQuote />, action: () => formatBlock('blockquote'), tooltip: 'Quote' },
    { icon: <FormatListBulleted />, command: 'insertUnorderedList', tooltip: 'Bullet List' },
    { icon: <FormatListNumbered />, command: 'insertOrderedList', tooltip: 'Numbered List' },
    { icon: <FormatAlignLeft />, command: 'justifyLeft', tooltip: 'Align Left' },
    { icon: <FormatAlignCenter />, command: 'justifyCenter', tooltip: 'Align Center' },
    { icon: <FormatAlignRight />, command: 'justifyRight', tooltip: 'Align Right' },
    { icon: <Link />, action: () => setLinkDialog(true), tooltip: 'Insert Link' },
    { icon: <Image />, action: () => setImageDialog(true), tooltip: 'Insert Image' },
    { icon: <YouTube />, action: () => setYoutubeDialog(true), tooltip: 'Insert YouTube Video' },
    { icon: <Code />, action: () => formatBlock('pre'), tooltip: 'Code Block' },
    { icon: <Undo />, command: 'undo', tooltip: 'Undo (Ctrl+Z)' },
    { icon: <Redo />, command: 'redo', tooltip: 'Redo (Ctrl+Y)' }
  ];

  return (
    <>
      <Paper variant="outlined">
        <Box display="flex" flexWrap="wrap" p={1} borderBottom="1px solid #e0e0e0" gap={0.5}>
          {toolbarButtons.map((button, index) => (
            button.custom ? (
              <Tooltip key={index} title={button.tooltip}>
                {button.custom}
              </Tooltip>
            ) : (
              <Tooltip key={index} title={button.tooltip}>
                <IconButton
                  size="small"
                  onClick={() => button.action ? button.action() : execCommand(button.command, button.value)}
                  sx={{ 
                    border: '1px solid transparent',
                    '&:hover': { border: '1px solid #ccc' }
                  }}
                >
                  {button.icon}
                </IconButton>
              </Tooltip>
            )
          ))}
        </Box>
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          data-placeholder={placeholder}
          dir="ltr"
          style={{
            minHeight: 300,
            padding: 16,
            border: 'none',
            outline: 'none',
            direction: 'ltr',
            textAlign: 'left',
            unicodeBidi: 'plaintext',
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: 1.6,
            overflow: 'auto'
          }}
          sx={{
            '&:empty:before': {
              content: 'attr(data-placeholder)',
              color: '#999',
              fontStyle: 'italic'
            },
            '& *': {
              direction: 'ltr !important',
              textAlign: 'left !important'
            }
          }}
        />
      </Paper>

      {/* Link Dialog */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
          <TextField
            margin="dense"
            label="Link Text (optional)"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="Link text"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>Cancel</Button>
          <Button onClick={handleLinkInsert} variant="contained">Insert</Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image URL"
            fullWidth
            variant="outlined"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
          <TextField
            margin="dense"
            label="Alt Text (optional)"
            fullWidth
            variant="outlined"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            placeholder="Image description"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog(false)}>Cancel</Button>
          <Button onClick={handleImageInsert} variant="contained">Insert</Button>
        </DialogActions>
      </Dialog>

      {/* YouTube Dialog */}
      <Dialog open={youtubeDialog} onClose={() => setYoutubeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert YouTube Video</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="YouTube URL"
            fullWidth
            variant="outlined"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Paste a YouTube URL and it will be embedded as a video player
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setYoutubeDialog(false)}>Cancel</Button>
          <Button onClick={handleYouTubeInsert} variant="contained">Insert</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RichTextEditor;